import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { createLinqset } from '../../common/model-processing';
import { createInsightsFilter, Organisation } from '../../common/organisation';
import { NOTIFICATION_TYPE, Notification, USER_ROLE, getNotificationTypeName } from '../../common/user';
import { FUNCTION, FunctionService } from '../../model-services/function.service';
import { UserService } from '../../user/user.service';

class NotificationGroup {
    public name: string
    public notifications: Notification[]

    constructor(public type: NOTIFICATION_TYPE) {
        this.name = getNotificationTypeName(type)
        this.notifications = []
    }

    addNotification(n:Notification) {
        this.notifications.push(n)
    }
}

// the bell in the menu bar, with a count of unread notifications, and the list of notifications it opens.
// Opening the list marks them as read; access requests can be acted on by the model or project's administrators
@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.html',
    styleUrls: ['./notifications.css'],
    standalone: false
})
export class NotificationsComponent implements OnInit, OnChanges, OnDestroy {
    @Input() organisation:Organisation

    @ViewChild('notificationButton', {read: ElementRef}) private notificationButton: ElementRef;
    @ViewChild('notificationOverlay') notificationOverlayTemplate: TemplateRef<any>;

    isOpen = false;
    groups:NotificationGroup[] = []
    notifications:Notification[] = []
    deleting = new Set<string>()

    overlayRef:OverlayRef
    notificationsSub:Subscription

    constructor(private functionService:FunctionService, private userService:UserService, private overlay: Overlay,
        private viewContainerRef: ViewContainerRef) {}

    ngOnDestroy(): void {
        if (this.notificationsSub) this.notificationsSub.unsubscribe()
        this.close()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['organisation']) {
            this.createNotificationGroups()
        }
    }

    ngOnInit() {
        // notifications may have loaded before this bell was shown
        this.notifications = this.userService.notifications || []
        this.createNotificationGroups()
        this.notificationsSub = this.userService.notificationsEvent.subscribe(() => {
            this.notifications = this.userService.notifications || []
            this.createNotificationGroups()
        })
    }

    // only notifications that are still shown count
    get unreadCount() {
        return this.groups.reduce((count, g) => count + g.notifications.filter(n => !n.read).length, 0)
    }

    get buttonLabel() {
        const unread = this.unreadCount
        return unread ? `Notifications, ${unread} unread` : 'Notifications'
    }

    @HostListener('keydown.esc', ['$event'])
    onEsc(event: any) {
        this.close()
    }

    open() {
        if (this.isOpen) return
        this.isOpen = true
        const positionStrategy = this.overlay.position()
            .flexibleConnectedTo(this.notificationButton)
            .withPositions([
                {originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top'},
                {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top'},
            ])
            .withPush(true)
            .withViewportMargin(8);

        this.overlayRef = this.overlay.create({
            hasBackdrop: true,
            backdropClass: "cdk-overlay-transparent-backdrop",
            positionStrategy,
            maxWidth: '95vw',
            scrollStrategy: this.overlay.scrollStrategies.reposition()
        });

        this.overlayRef.attach(new TemplatePortal(this.notificationOverlayTemplate, this.viewContainerRef, {}));
        this.overlayRef.backdropClick().subscribe(() => this.close());
        this.overlayRef.keydownEvents().subscribe(e => { if (e.key === 'Escape') this.close() })

        this.markAsRead()
    }

    // seeing the list counts as reading it; if saving that fails, the count comes back next time
    private async markAsRead() {
        if (this.unreadCount === 0) return
        try {
            await this.userService.updateNotifications()
        }catch(err) {
            // the error has already been shown
        }
    }

    close() {
        this.isOpen = false
        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.overlayRef = null;
        }
    }

    createNotificationGroups() {
        const now = Date.now()
        const current = (this.notifications || []).filter(n => !n.expiry || new Date(n.expiry).getTime() > now)
        const groupMap = current.reduce((g:any, n:Notification) => {
            if (!g[n.type]) g[n.type] = new NotificationGroup(n.type)
            g[n.type].addNotification(n)
            return g
        }, {})

        this.groups = Object.values(groupMap)
    }

    // what can be done about a notification here, if anything
    getAction(notification:Notification):string|undefined {
        if (!this.organisation || notification.organisationKey !== this.organisation._key) return undefined
        if (notification.type === NOTIFICATION_TYPE.REQUEST_FOR_MODEL_ACCESS) {
            const model = this.organisation.models?.find(m => m._key === notification.referenceKey)
            return model?.role === USER_ROLE.ADMIN ? 'Manage model access' : undefined
        }
        if (notification.type === NOTIFICATION_TYPE.REQUEST_FOR_PROJECT_ACCESS) {
            const project = this.organisation.projects?.find(p => p._key === notification.referenceKey)
            return project?.role === USER_ROLE.ADMIN ? 'Manage project access' : undefined
        }
        return undefined
    }

    // explains why a notification can't be acted on here
    getActionNote(notification:Notification):string|undefined {
        if (this.getAction(notification)) return undefined
        if (notification.type !== NOTIFICATION_TYPE.REQUEST_FOR_MODEL_ACCESS && notification.type !== NOTIFICATION_TYPE.REQUEST_FOR_PROJECT_ACCESS) return undefined
        if (!this.organisation || notification.organisationKey !== this.organisation._key) return `Open ${notification.organisationName || 'that organisation'} to act on this`
        return 'Only an administrator can change access'
    }

    select(notification:Notification) {
        if (!this.getAction(notification)) return

        if (notification.type === NOTIFICATION_TYPE.REQUEST_FOR_MODEL_ACCESS) {
            const model = this.organisation.models.find(m => m._key === notification.referenceKey)
            const filter = createInsightsFilter()
            filter.modelKeys = [model._key]
            const ls = createLinqset(this.organisation, filter)
            this.functionService.executeFunction(FUNCTION.EDIT_MODEL_DETAILS, undefined, ls)
        }else if (notification.type === NOTIFICATION_TYPE.REQUEST_FOR_PROJECT_ACCESS) {
            const project = this.organisation.projects.find(p => p._key === notification.referenceKey)
            this.functionService.executeFunction(FUNCTION.EDIT_PROJECT, undefined, undefined, project)
        }
        this.close()
    }

    // the list stays open, so several can be cleared in turn
    async deleteNotification(notification:Notification) {
        if (this.deleting.has(notification._key)) return
        this.deleting.add(notification._key)
        try {
            await this.userService.deleteNotification(notification)
        }catch(err) {
            // the error has already been shown
        }finally {
            this.deleting.delete(notification._key)
        }
    }
}
